import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Statistic, Button, List, Space, message, Progress, Row, Col, Typography, Modal, Image } from 'antd';
import { PlusOutlined, MinusOutlined, FilePdfOutlined, FileImageOutlined, FileTextOutlined, EyeOutlined } from '@ant-design/icons';
import { doc, getDoc, collection, addDoc, query, where, orderBy, getDocs, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, isToday } from 'date-fns';
import FileAttachments from './FileAttachments';

const { Title, Text, Paragraph } = Typography;

const ParayanaDetail = () => {
  const { parayanaId } = useParams();
  const navigate = useNavigate();
  const { getUserId, getUserEmail } = useAuth();
  const [parayana, setParayana] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weeklyProgress, setWeeklyProgress] = useState([]);
  const [filePreviewVisible, setFilePreviewVisible] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');

  const fetchParayana = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;
    try {
      console.log(`📖 Fetching parayana ${parayanaId} for user ${userId}`);
      const docRef = doc(db, `users/${userId}/parayanas`, parayanaId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        setParayana({ id: docSnap.id, ...data });
        console.log('✅ Parayana loaded successfully');
      } else {
        console.log('❌ Parayana not found');
        message.error('Parayana not found.');
        navigate('/apps/prayer-counter');
      }
    } catch (error) {
      console.error('💥 Error fetching parayana:', error);
      message.error('Error fetching parayana');
      navigate('/apps/prayer-counter');
    }
  }, [parayanaId, navigate, getUserId]);

  const fetchTransactions = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;
    try {
      console.log(`📊 Fetching transactions for parayana ${parayanaId}`);
      const q = query(
        collection(db, `users/${userId}/parayanas/${parayanaId}/logs`),
        orderBy('timestamp', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const transactionList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log(`📋 Loaded ${transactionList.length} transactions`);
      setTransactions(transactionList);
      
      // Calculate weekly progress for analytics
      calculateWeeklyProgress(transactionList);
    } catch (error) {
      console.log('⚠️ Ordered query failed, trying without orderBy');
      // Try without orderBy in case index is missing
      try {
        const qNoOrder = query(
          collection(db, `users/${userId}/parayanas/${parayanaId}/logs`)
        );
        const querySnapshot = await getDocs(qNoOrder);
        const transactionList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort manually by timestamp
        transactionList.sort((a, b) => {
          const dateA = a.timestamp?.toDate?.() || new Date(0);
          const dateB = b.timestamp?.toDate?.() || new Date(0);
          return dateB - dateA;
        });
        
        console.log(`📋 Loaded ${transactionList.length} transactions (manual sort)`);
        setTransactions(transactionList);
        calculateWeeklyProgress(transactionList);
      } catch (fallbackError) {
        console.error('💥 Error fetching transactions:', fallbackError);
        setTransactions([]);
      }
    } finally {
      setLoading(false);
    }
  }, [parayanaId, getUserId]);

  const calculateWeeklyProgress = (transactionList) => {
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 0 }); // Start from Sunday
    const weekEnd = endOfWeek(now, { weekStartsOn: 0 });
    const daysOfWeek = eachDayOfInterval({ start: weekStart, end: weekEnd });
    
    const weeklyData = daysOfWeek.map(day => {
      const dayTransactions = transactionList.filter(t => {
        if (!t.timestamp) return false;
        
        let transactionDate;
        try {
          // Handle both Firestore Timestamp and regular Date objects
          if (t.timestamp.toDate && typeof t.timestamp.toDate === 'function') {
            transactionDate = t.timestamp.toDate();
          } else {
            transactionDate = t.timestamp instanceof Date ? t.timestamp : new Date(t.timestamp);
          }
          
          const isSameDay = transactionDate.toDateString() === day.toDateString();
          return isSameDay;
        } catch (error) {
          return false;
        }
      });
      
      const totalCount = dayTransactions.reduce((sum, t) => {
        const delta = t.countDelta || 0;
        return sum + Math.max(0, delta); // Only count positive increments
      }, 0);
      
      return {
        day: format(day, 'EEE'),
        fullDay: format(day, 'EEEE'),
        date: day,
        count: totalCount,
        isToday: isToday(day)
      };
    });
    
    setWeeklyProgress(weeklyData);
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchParayana();
      await fetchTransactions();
    };
    loadData();
  }, [fetchParayana, fetchTransactions]);

  const handleCountChange = async (delta) => {
    const userId = getUserId();
    if (!userId) {
      message.error('User not authenticated');
      return;
    }
    
    // Validate delta value
    if (!Number.isInteger(delta) || delta === 0) {
      message.error('Invalid count change value');
      return;
    }
    
    // Additional validation for the current parayana state
    if (!parayana) {
      message.error('Parayana data not loaded');
      return;
    }
    
    const currentCount = parayana.currentCount || 0;
    
    // Prevent negative counts
    if (delta < 0 && currentCount + delta < 0) {
      message.warning('Count cannot go below zero');
      return;
    }
    
    // Prevent excessive counts (safety limit)
    if (delta > 0 && currentCount + delta > 999999) {
      message.warning('Count limit reached (999,999)');
      return;
    }
    
    // Warn if trying to increment already completed parayana
    if (delta > 0 && currentCount >= parayana.targetCount) {
      message.info('This parayana is already completed! 🎉');
      return;
    }
    
    try {
      console.log(`📊 Updating count by ${delta} for parayana ${parayanaId}`);
      
      // Log the transaction first
      const logRef = await addDoc(collection(db, `users/${userId}/parayanas/${parayanaId}/logs`), {
        countDelta: delta,
        timestamp: serverTimestamp(),
        uid: userId,
        parayanaName: parayana.name // For debugging and analytics
      });

      // Calculate new count
      const newCount = Math.max(0, currentCount + delta);
      
      // Update the parayana count
      const parayanaRef = doc(db, `users/${userId}/parayanas`, parayanaId);
      await updateDoc(parayanaRef, {
        currentCount: newCount,
        lastUpdated: serverTimestamp()
      });

      // Update local state immediately for better UX
      setTransactions(prev => [{
        id: logRef.id,
        countDelta: delta,
        timestamp: new Date(),
        uid: userId,
        parayanaName: parayana.name
      }, ...prev]);

      setParayana(prev => ({ ...prev, currentCount: newCount }));

      // Show appropriate success message
      const isNowCompleted = newCount >= parayana.targetCount && delta > 0;
      
      if (isNowCompleted) {
        message.success('🎉 Congratulations! Parayana completed! 🙏', 3);
      } else {
        message.success(`Count ${delta > 0 ? 'increased' : 'decreased'} successfully! 🙏`);
      }
      
      // Recalculate weekly progress with updated data
      const updatedTransactions = [{
        id: logRef.id,
        countDelta: delta,
        timestamp: new Date(),
        uid: userId,
        parayanaName: parayana.name
      }, ...transactions];
      calculateWeeklyProgress(updatedTransactions);
      
    } catch (error) {
      console.error('💥 Error updating count:', error);
      
      // Provide specific error messages
      if (error.code === 'permission-denied') {
        message.error('Permission denied. Please check your authentication.');
      } else if (error.code === 'unavailable') {
        message.error('Service temporarily unavailable. Please try again.');
      } else if (error.code === 'quota-exceeded') {
        message.error('Daily quota exceeded. Please try again tomorrow.');
      } else if (error.code === 'not-found') {
        message.error('Parayana not found. It may have been deleted.');
        navigate('/apps/prayer-counter');
      } else if (error.message.includes('network')) {
        message.error('Network error. Please check your connection.');
      } else {
        message.error(`Update failed: ${error.message}`);
      }
    }
  };

  // File preview functionality
  const handleFilePreview = async (file) => {
    if (!file.url) return;
    
    setPreviewTitle(file.name);
    
    if (file.type?.includes('image')) {
      setPreviewContent(<Image src={file.url} style={{ maxWidth: '100%' }} />);
    } else if (file.type?.includes('pdf')) {
      setPreviewContent(
        <iframe 
          src={file.url} 
          style={{ width: '100%', height: '500px', border: 'none' }}
          title={file.name}
        />
      );
    } else {
      // For text files, try to fetch and display content
      try {
        const response = await fetch(file.url);
        const text = await response.text();
        setPreviewContent(
          <Paragraph 
            style={{ 
              maxHeight: '400px', 
              overflow: 'auto', 
              padding: '16px',
              background: '#f5f5f5',
              borderRadius: '8px',
              whiteSpace: 'pre-wrap'
            }}
          >
            {text}
          </Paragraph>
        );
      } catch (error) {
        setPreviewContent(
          <Text type="secondary">Cannot preview this file type. Click the file name to download.</Text>
        );
      }
    }
    
    setFilePreviewVisible(true);
  };

  const getFileIcon = (type) => {
    if (type?.includes('pdf')) return <FilePdfOutlined style={{ color: '#ff4757', fontSize: '16px' }} />;
    if (type?.includes('image')) return <FileImageOutlined style={{ color: '#2ed573', fontSize: '16px' }} />;
    return <FileTextOutlined style={{ color: '#3742fa', fontSize: '16px' }} />;
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        height: '200px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🕉️</div>
          <Text style={{ color: '#FF8C00', fontSize: '16px' }}>Loading...</Text>
        </div>
      </div>
    );
  }

  if (!parayana) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        height: '200px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
          <Text style={{ color: '#FF8C00', fontSize: '16px' }}>Parayana not found</Text>
        </div>
      </div>
    );
  }

  const currentCount = parayana.currentCount || 0;
  const progress = Math.min((currentCount / parayana.targetCount) * 100, 100);
  const isCompleted = currentCount >= parayana.targetCount;
  const maxWeeklyCount = Math.max(...weeklyProgress.map(d => d.count), 1);
  const attachments = parayana.attachments || [];

  return (
    <div style={{ 
      background: 'transparent',
      minHeight: 'calc(100vh - 60px)',
      padding: '0'
    }}>
      {/* Header with parayana info */}
      <Card
        style={{ 
          marginBottom: 12,
          background: 'linear-gradient(135deg, #FFF4E6 0%, #FFE4B5 100%)',
          border: '1px solid #FFB347',
          borderRadius: '12px'
        }}
        styles={{ body: { padding: '12px 16px' } }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div>
            <Title level={4} style={{ margin: 0, color: '#FF8C00', fontSize: '18px' }}>
              🕉️ {parayana.name}
            </Title>
            <Text style={{ color: '#FF7F00', fontSize: '12px' }}>
              Started on {format(parayana.startDate?.toDate() || new Date(), 'PPP')}
              {getUserEmail() && (
                <span style={{ marginLeft: '8px', fontSize: '11px', opacity: 0.8 }}>
                  • {getUserEmail()}
                </span>
              )}
            </Text>
          </div>
        </div>
      </Card>

      <Row gutter={[12, 12]}>
        {/* Main Counter Card */}
        <Col xs={24} lg={attachments.length > 0 ? 10 : 14}>
          <Card 
            style={{
              borderRadius: '16px',
              border: `2px solid ${isCompleted ? '#52c41a' : '#FFB347'}`,
              boxShadow: `0 4px 16px ${isCompleted ? '#52c41a' : '#FF8C00'}20`,
              background: isCompleted 
                ? 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)'
                : 'linear-gradient(135deg, #FFF8F0 0%, #FFF4E6 100%)'
            }}
            styles={{ body: { padding: '20px' } }}
          >
            {/* Progress Display */}
            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
              <Progress
                type="circle"
                percent={Math.round(progress)}
                size={100}
                strokeColor={isCompleted ? '#52c41a' : '#FF8C00'}
                trailColor="#f0f0f0"
                strokeWidth={8}
                format={() => (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      fontSize: '18px', 
                      fontWeight: 'bold',
                      color: isCompleted ? '#52c41a' : '#FF7F00'
                    }}>
                      {currentCount}
                    </div>
                    <div style={{ fontSize: '11px', color: '#666' }}>
                      of {parayana.targetCount}
                    </div>
                  </div>
                )}
              />
              {isCompleted && (
                <div style={{ 
                  marginTop: '12px',
                  fontSize: '18px',
                  color: '#52c41a',
                  fontWeight: 'bold'
                }}>
                  🎉 Completed! 🎉
                </div>
              )}
            </div>

            {/* Counter Controls */}
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <Space size="large">
                <Button
                  icon={<MinusOutlined />}
                  onClick={() => handleCountChange(-1)}
                  disabled={currentCount <= 0}
                  style={{
                    borderColor: '#FF8C00',
                    color: '#FF8C00',
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  size="large"
                />
                
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => handleCountChange(1)}
                  disabled={isCompleted}
                  style={{
                    background: 'linear-gradient(135deg, #FF8C00 0%, #FF7F00 100%)',
                    borderColor: '#FF8C00',
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px'
                  }}
                  size="large"
                />
              </Space>
            </div>

            {/* Statistics */}
            <Row gutter={12}>
              <Col span={8}>
                <Statistic
                  title="Progress"
                  value={progress}
                  suffix="%"
                  precision={1}
                  valueStyle={{ color: isCompleted ? '#52c41a' : '#FF7F00', fontSize: '16px' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Remaining"
                  value={Math.max(0, parayana.targetCount - currentCount)}
                  valueStyle={{ color: '#FF7F00', fontSize: '16px' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Total Days"
                  value={Math.ceil((new Date() - parayana.startDate?.toDate()) / (1000 * 60 * 60 * 24))}
                  valueStyle={{ color: '#FF7F00', fontSize: '16px' }}
                />
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Study Materials Card */}
        {attachments.length > 0 && (
          <Col xs={24} lg={4}>
            <Card 
              title={
                <span style={{ color: '#FF8C00', fontWeight: '500', fontSize: '16px' }}>
                  📚 Study Materials
                </span>
              }
              style={{ 
                borderRadius: '12px',
                border: '1px solid #FFB347',
                marginBottom: '12px'
              }}
              styles={{ body: { padding: '16px' } }}
            >
              <List
                dataSource={attachments.slice(0, 6)} // Show max 6 files
                renderItem={(file) => (
                  <List.Item 
                    style={{ 
                      padding: '8px 0',
                      borderBottom: '1px solid #f0f0f0',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleFilePreview(file)}
                  >
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      width: '100%'
                    }}>
                      {getFileIcon(file.type)}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text 
                          style={{ 
                            fontSize: '12px',
                            color: '#333',
                            display: 'block'
                          }}
                          ellipsis={{ tooltip: file.name }}
                        >
                          {file.name}
                        </Text>
                        <Text 
                          style={{ 
                            fontSize: '10px', 
                            color: '#999' 
                          }}
                        >
                          {file.size ? `${(file.size / 1024).toFixed(1)} KB` : ''}
                        </Text>
                      </div>
                      <EyeOutlined style={{ color: '#FF8C00', fontSize: '12px' }} />
                    </div>
                  </List.Item>
                )}
              />
              {attachments.length > 6 && (
                <div style={{ 
                  textAlign: 'center', 
                  marginTop: '8px',
                  fontSize: '12px',
                  color: '#999'
                }}>
                  +{attachments.length - 6} more files
                </div>
              )}
            </Card>
          </Col>
        )}

        {/* File Attachments Modal */}
        <FileAttachments
          parayanaId={parayanaId}
          attachments={attachments}
          onAttachmentsChange={() => {}} // Read-only in detail view
        />

        {/* Analytics Card */}
        <Col xs={24} lg={attachments.length > 0 ? 10 : 10}>
          <Card 
            title={
              <span style={{ color: '#FF8C00', fontWeight: '500', fontSize: '16px' }}>
                📊 This Week's Progress
              </span>
            }
            style={{ 
              borderRadius: '12px',
              border: '1px solid #FFB347',
              marginBottom: '12px'
            }}
            styles={{ body: { padding: '16px' } }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '4px', marginBottom: '8px' }}>
              {weeklyProgress.map((day, index) => (
                <div 
                  key={index}
                  style={{ 
                    textAlign: 'center',
                    flex: 1,
                    padding: '4px 2px'
                  }}
                >
                  <div style={{ 
                    fontSize: '11px', 
                    color: day.isToday ? '#FF8C00' : '#666', 
                    marginBottom: '4px',
                    fontWeight: day.isToday ? 'bold' : 'normal'
                  }}>
                    {day.day}
                  </div>
                  <div 
                    style={{ 
                      height: `${Math.max(8, (day.count / maxWeeklyCount) * 60)}px`,
                      background: day.count > 0 
                        ? `linear-gradient(135deg, ${day.isToday ? '#FF7F00' : '#FF8C00'} 0%, ${day.isToday ? '#FF6B00' : '#FF7F00'} 100%)`
                        : '#f0f0f0',
                      borderRadius: '4px',
                      marginBottom: '4px',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      border: day.isToday ? '2px solid #FF8C00' : 'none',
                      position: 'relative'
                    }}
                    title={`${day.fullDay}: ${day.count} count${day.count !== 1 ? 's' : ''}`}
                  >
                    {day.count > 0 && (
                      <span style={{ 
                        position: 'absolute',
                        bottom: '2px',
                        fontSize: '9px'
                      }}>
                        {day.count}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ 
              textAlign: 'center', 
              fontSize: '11px', 
              color: '#999',
              marginTop: '8px'
            }}>
              Weekly Total: {weeklyProgress.reduce((sum, day) => sum + day.count, 0)} counts
            </div>
          </Card>

          {/* Recent Transactions */}
          <Card 
            title={
              <span style={{ color: '#FF8C00', fontWeight: '500', fontSize: '16px' }}>
                📜 Recent Activity
              </span>
            }
            style={{ 
              borderRadius: '12px',
              border: '1px solid #FFB347'
            }}
            styles={{ body: { padding: '16px' } }}
          >
            <List
              dataSource={transactions.slice(0, 8)}
              renderItem={item => {
                // Handle both Firestore Timestamp and regular Date objects
                const getTimestamp = (timestamp) => {
                  if (!timestamp) return new Date();
                  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
                    // Firestore Timestamp
                    return timestamp.toDate();
                  }
                  // Regular Date object
                  return timestamp instanceof Date ? timestamp : new Date(timestamp);
                };

                return (
                  <List.Item style={{ 
                    borderColor: '#FFE4B5',
                    padding: '8px 0'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      width: '100%'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: item.countDelta > 0 
                            ? 'linear-gradient(135deg, #FF8C00 0%, #FF7F00 100%)'
                            : 'linear-gradient(135deg, #ff7875 0%, #ff4d4f 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          {item.countDelta > 0 ? '+' : ''}{item.countDelta}
                        </div>
                        <div>
                          <div style={{ 
                            fontSize: '13px',
                            fontWeight: '500',
                            color: '#333'
                          }}>
                            {item.countDelta > 0 ? 'Added' : 'Removed'} {Math.abs(item.countDelta)} count{Math.abs(item.countDelta) !== 1 ? 's' : ''}
                            {item.countDelta > 0 ? ' 🙏' : ''}
                          </div>
                          <div style={{ 
                            fontSize: '11px', 
                            color: '#999'
                          }}>
                            {format(getTimestamp(item.timestamp), 'MMM dd, yyyy • HH:mm')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </List.Item>
                );
              }}
              locale={{
                emptyText: (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>📝</div>
                    <Text style={{ color: '#999' }}>No activity yet. Start counting!</Text>
                  </div>
                )
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* File Preview Modal */}
      <Modal
        open={filePreviewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setFilePreviewVisible(false)}
        width={800}
        style={{ top: 20 }}
      >
        {previewContent}
      </Modal>
    </div>
  );
};

export default ParayanaDetail; 